import { useEffect, useRef, useState } from "react";

const CHANNEL_NAME = "coderom-leader-v1";
const HEARTBEAT_INTERVAL_MS = 2000;
const HEARTBEAT_TIMEOUT_MS = 6000;
const ELECTION_WAIT_MS = 300;
const FOLLOWER_CHECK_INTERVAL_MS = 1000;

type LeaderMessage =
  | { type: "HEARTBEAT"; tabId: string; ts: number }
  | { type: "ABDICATE"; tabId: string }
  | { type: "CLAIM"; tabId: string }
  | { type: "PING"; tabId: string }
  | { type: "PONG"; tabId: string; ts: number };

interface UseLeaderElectionResult {
  isLeader: boolean;
  tabId: string;
  leaderTabId: string | null;
}

function generateTabId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof (crypto as Crypto).randomUUID === "function"
  ) {
    return (crypto as Crypto).randomUUID();
  }
  return `tab-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function useLeaderElection(): UseLeaderElectionResult {
  const tabIdRef = useRef<string>(generateTabId());

  const [isLeader, setIsLeader] = useState<boolean>(false);
  const [leaderTabId, setLeaderTabId] = useState<string | null>(null);

  const channelRef = useRef<BroadcastChannel | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const followerCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const electionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialElectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLeaderRef = useRef<boolean>(false);
  const isElectingRef = useRef<boolean>(false);
  const leaderTabIdRef = useRef<string | null>(null);
  const lastHeartbeatAtRef = useRef<number>(0);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      console.warn(
        "[LeaderElection] BroadcastChannel not supported; defaulting to leader mode"
      );
      isLeaderRef.current = true;
      leaderTabIdRef.current = tabIdRef.current;
      setIsLeader(true);
      setLeaderTabId(tabIdRef.current);
      return;
    }

    const myTabId = tabIdRef.current;
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    const post = (msg: LeaderMessage): void => {
      try {
        channel.postMessage(msg);
      } catch (err) {
        console.warn("[LeaderElection] postMessage failed:", err);
      }
    };

    const clearElection = (): void => {
      if (electionTimeoutRef.current !== null) {
        clearTimeout(electionTimeoutRef.current);
        electionTimeoutRef.current = null;
      }
    };

    const stopHeartbeat = (): void => {
      if (heartbeatIntervalRef.current !== null) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };

    const startHeartbeat = (): void => {
      stopHeartbeat();
      post({ type: "HEARTBEAT", tabId: myTabId, ts: Date.now() });
      heartbeatIntervalRef.current = setInterval(() => {
        post({ type: "HEARTBEAT", tabId: myTabId, ts: Date.now() });
      }, HEARTBEAT_INTERVAL_MS);
    };

    const becomeLeader = (): void => {
      console.log(`[Leader] Became LEADER tabId=${myTabId}`);
      isElectingRef.current = false;
      clearElection();
      isLeaderRef.current = true;
      leaderTabIdRef.current = myTabId;
      setIsLeader(true);
      setLeaderTabId(myTabId);
      startHeartbeat();
    };

    const becomeFollower = (newLeaderId: string | null): void => {
      console.log(`[Leader] Became FOLLOWER leaderTabId=${newLeaderId ?? 'unknown'}`);
      isElectingRef.current = false;
      clearElection();
      stopHeartbeat();
      isLeaderRef.current = false;
      leaderTabIdRef.current = newLeaderId;
      setIsLeader(false);
      setLeaderTabId(newLeaderId);
    };

    const startElection = (): void => {
      clearElection();
      isElectingRef.current = true;
      post({ type: "CLAIM", tabId: myTabId });
      electionTimeoutRef.current = setTimeout(() => {
        if (isElectingRef.current) {
          becomeLeader();
        }
      }, ELECTION_WAIT_MS);
    };

    const abdicate = (): void => {
      if (!isLeaderRef.current) return;
      post({ type: "ABDICATE", tabId: myTabId });
      stopHeartbeat();
      isLeaderRef.current = false;
      leaderTabIdRef.current = null;
      setIsLeader(false);
      setLeaderTabId(null);
    };

    const onMessage = (event: MessageEvent<LeaderMessage>): void => {
      const msg = event.data;
      if (!msg || typeof msg !== "object" || !("type" in msg)) return;
      if ("tabId" in msg && msg.tabId === myTabId) return;

      switch (msg.type) {
        case "HEARTBEAT": {
          lastHeartbeatAtRef.current = msg.ts;

          if (isElectingRef.current) {
            becomeFollower(msg.tabId);
            return;
          }

          if (isLeaderRef.current) {
            // İki leader çakışması — yüksek tabId kazanır (deterministik)
            if (msg.tabId > myTabId) {
              becomeFollower(msg.tabId);
            }
            return;
          }

          // Follower modda — leader değişimini takip et
          if (leaderTabIdRef.current !== msg.tabId) {
            leaderTabIdRef.current = msg.tabId;
            setLeaderTabId(msg.tabId);
          }
          return;
        }
        case "CLAIM": {
          // EC-2: Aynı anda election → tabId karşılaştırması (lexicographic, UUID deterministik)
          if (isElectingRef.current) {
            if (msg.tabId > myTabId) {
              becomeFollower(null);
            }
            return;
          }

          // Mevcut leader: yeni sekmeye anında "ben varım" de
          // Yeni sekme 300ms dolmadan HEARTBEAT'i görür → becomeFollower → çift SSE açılmaz
          if (isLeaderRef.current) {
            post({ type: "HEARTBEAT", tabId: myTabId, ts: Date.now() });
            return;
          }

          // Follower isem mevcut leader'ın heartbeat'i devam ediyor — aksiyon yok
          return;
        }
        case "ABDICATE": {
          // Mevcut leader çekildiyse hemen election başlat (6sn timeout beklemeden)
          if (msg.tabId === leaderTabIdRef.current) {
            leaderTabIdRef.current = null;
            setLeaderTabId(null);
            lastHeartbeatAtRef.current = 0;
            startElection();
          }
          return;
        }
        case "PING": {
          if (isLeaderRef.current) {
            post({ type: "PONG", tabId: myTabId, ts: Date.now() });
          }
          return;
        }
        case "PONG": {
          // Bilgilendirici — şimdilik kullanım yok
          return;
        }
      }
    };

    channel.addEventListener("message", onMessage);

    // Follower timeout watcher: lider 6sn'den uzun süredir heartbeat göndermediyse election başlat
    followerCheckIntervalRef.current = setInterval(() => {
      if (isLeaderRef.current || isElectingRef.current) return;
      if (lastHeartbeatAtRef.current === 0) return;
      const elapsed = Date.now() - lastHeartbeatAtRef.current;
      if (elapsed > HEARTBEAT_TIMEOUT_MS) {
        startElection();
      }
    }, FOLLOWER_CHECK_INTERVAL_MS);

    const onVisibility = (): void => {
      if (document.hidden) {
        // Hidden olunca abdicate etme — heartbeat arka planda
        // devam eder (browser 1Hz'e throttle eder ama yine atar).
        // Gereksiz SSE close/reopen + fetchOrders(10000) engellenir.
        // Multi-tab failover ABDICATE mesajına değil, heartbeat
        // timeout'a (6sn) bırakılır.
        return;
      }

      // Visible olunca: gerçekten gerekiyorsa election yap
      if (isLeaderRef.current) {
        // Hâlâ liderim — no-op
        return;
      }

      // Follower'sam: leader yaşıyor mu?
      const elapsed = Date.now() - lastHeartbeatAtRef.current;
      if (lastHeartbeatAtRef.current === 0 || elapsed > HEARTBEAT_TIMEOUT_MS) {
        // Leader yok veya timeout — election başlat
        startElection();
      }
      // Aksi halde leader yaşıyor, election'a gerek yok
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Sayfa kapanırken best-effort ABDICATE (cleanup'ta da gönderilecek ama beforeunload daha erken)
    const onBeforeUnload = (): void => {
      if (isLeaderRef.current) {
        const abdicateMsg: LeaderMessage = { type: "ABDICATE", tabId: myTabId };
        try {
          channel.postMessage(abdicateMsg);
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    // İlk election: sayfa yüklenirken event loop meşgul olabileceğinden
    // kısa gecikme ile başlat — böylece mevcut leader'ın HEARTBEAT yanıtı
    // 300ms election window'una yetişebilir.
    // Sonraki election'lar (ABDICATE, timeout, visibility) gecikmesiz.
    if (!document.hidden) {
      initialElectionTimerRef.current = setTimeout(startElection, 500);
    }

    return () => {
      channel.removeEventListener("message", onMessage);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);

      if (isLeaderRef.current) {
        const abdicateMsg: LeaderMessage = { type: "ABDICATE", tabId: myTabId };
        try {
          channel.postMessage(abdicateMsg);
        } catch {
          // ignore
        }
      }

      stopHeartbeat();
      clearElection();
      if (initialElectionTimerRef.current !== null) {
        clearTimeout(initialElectionTimerRef.current);
        initialElectionTimerRef.current = null;
      }
      if (followerCheckIntervalRef.current !== null) {
        clearInterval(followerCheckIntervalRef.current);
        followerCheckIntervalRef.current = null;
      }

      try {
        channel.close();
      } catch {
        // ignore
      }
      channelRef.current = null;
      isLeaderRef.current = false;
      isElectingRef.current = false;
      leaderTabIdRef.current = null;
      lastHeartbeatAtRef.current = 0;
    };
  }, []);

  return {
    isLeader,
    tabId: tabIdRef.current,
    leaderTabId,
  };
}

export default useLeaderElection;
